import styled from "@emotion/styled";
import { PageLayout } from "../shared/layouts/PageLayout";
import { dashboardBreadcrumbs } from "../features/Dashboard/dashboard-breadcrumbs";
import { ManageSubscription } from "@features/Subscription/ManageSubscription";
import { useGetSubscriptionStatus } from "@queries/subscription/useGetSubscription";
import { Skeleton } from "@mui/material";
import { GetSubscriptionStatusResponse } from "@shared/apis/sidekick/generated";
import { useAuthStore } from "@store/auth.store";
import { ISubscriptionInfo } from "@features/Subscription/ManageSubscription/mange-subscription-models";
import { useCancelSubscription } from "@queries/subscription/useCancelSubscription";
import { useCallback } from "react";
import { useUIStore } from "@store/ui.store";

const breadcrumbs = {
  current: dashboardBreadcrumbs.accountBilling,
  previous: [dashboardBreadcrumbs.accountSettings],
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  padding: 2rem;
  border-radius: 1rem;
  color: ${(p) => p.theme.text.colors.primary};
  background-color: ${(p) => p.theme.colors.background};
  box-shadow: 0px 1px 3px rgba(0, 0, 0, 0.2);
  width: 100%;

  &[aria-disabled="true"] {
    opacity: 0.5;
    pointer-events: none;
  }
`;

export function ManageSubscriptionPage() {
  const query = useGetSubscriptionStatus({ enabled: true });
  const user = useAuthStore((state) => state.user);

  const cancelSubscriptionMutation = useCancelSubscription();

  const handleCancelSubscription = useCallback(async () => {
    if (query.data && query.data.type !== "paid") return;
    return cancelSubscriptionMutation.mutateAsync();
  }, [cancelSubscriptionMutation, query.data]);

  const parseSubscriptionInfo = (
    data: GetSubscriptionStatusResponse
  ): ISubscriptionInfo => {
    if (data.error) {
      return {
        type: "invalid" as const,
        name: "Sidekick Subscription",
        errorDescription:
          "There is an issue with your subscription, and it is currently not possible to make any change. If the problem persists, please contact support.",
        paymentMethod: null,
        billingDetails: null,
        actions: data.actions,
      };
    }
    if (data.type === "free") {
      return {
        type: "free" as const,
        name: "Sidekick Free Subscription",
        paymentMethod: null,
        billingDetails: null,
        actions: data.actions,
      };
    }
    if (data.type === "paid" && data.details) {
      const { details } = data;
      return {
        type: "paid" as const,
        name: "Sidekick Subscription",
        price: details.nextPaymentAmount,
        trialEndDate: details.trialEndDate ? details.trialEndDate : null,
        cancellationDate: details.cancellationDate ?? null,
        scheduledCancellationDate: details.scheduledCancellationDate ?? null,
        nextPaymentDate: details.nextPaymentDate ?? null,
        lastPaymentDate: details.lastPaymentDate ?? null,
        paymentStatus: details.paymentStatus,
        paymentMethodStatus: details.paymentMethodStatus,
        credits: details.credits ?? 0,
        estimatedPaymentMethodRequiredDate:
          details.estimatedPaymentMethodRequiredDate ?? null,
        paymentMethod: details.paymentMethod
          ? {
              type: details.paymentMethod.type,
              brand: details.paymentMethod.brand,
              last4: details.paymentMethod.last4,
              expMonth: details.paymentMethod.expiryMonth,
              expYear: details.paymentMethod.expiryYear,
            }
          : null,
        billingDetails: details.billingDetails?.state
          ? {
              address: {
                state: details.billingDetails.state ?? user.countryState,
                city: details.billingDetails.city ?? "",
                line1: details.billingDetails.line1 ?? "",
                line2: details.billingDetails.line2 ?? "",
                zipCode: details.billingDetails.zipCode ?? "",
                country: details.billingDetails.country ?? "US",
              },
            }
          : null,
        invoices: details.invoices ?? [],
        actions: data.actions,
      };
    }

    return {
      type: "invalid" as const,
      name: "Sidekick Subscription",
      errorDescription:
        "There is an issue with your subscription, and it is currently not possible to make any change. If the problem persists, please contact support.",
      paymentMethod: null,
      billingDetails: null,
    };
  };

  const toggleSupportDialogMode = useUIStore(
    (state) => state.toggleSupportDialogMode
  );

  const handleRestoreSubscriptionClick = useCallback(() => {
    toggleSupportDialogMode({
      mode: "support-request",
      initialValues: {
        message:
          "Hello, I’d like to request reactivation of my Sidekick subscription. Please let me know the next steps. Thank you!",
      },
    });
  }, [toggleSupportDialogMode]);

  return (
    <PageLayout breadcrumbs={breadcrumbs}>
      <Container>
        {query.isLoading && (
          <Skeleton variant="rounded" height={600} width={"100%"} />
        )}

        {!query.isLoading && query.data && (
          <ManageSubscription
            subscriptionInfo={parseSubscriptionInfo(query.data)}
            handleCancelSubscription={
              !query.data.details?.cancellationDate
                ? handleCancelSubscription
                : undefined
            }
            disabled={cancelSubscriptionMutation.isPending}
            onRestoreSubscriptionClick={
              query.data.details?.scheduledCancellationDate ||
              query.data.details?.cancellationDate
                ? handleRestoreSubscriptionClick
                : undefined
            }
            role="user"
          />
        )}
      </Container>
    </PageLayout>
  );
}
